import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { getApiUrl } from '../../utils/apiClient';
import { Home } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PageContainer from '../layout/PageContainer';


interface SalesData {
  date: string;
  total_sales: number;
  transaction_count: number;
  cash_sales: number;
  paypay_sales: number;
  eatin_sales: number;
  takeout_sales: number;
}

interface MenuSalesData {
  name: string;
  count?: number;
  revenue?: number;
  totalQuantity?: number;
  totalSales?: number;
}

interface HourlySalesData {
  hour: string;
  sales: number;
  transactions: number;
}

const SalesAnalytics: React.FC = () => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState<'day' | 'week' | 'month'>('week');
  const [salesData, setSalesData] = useState<SalesData[]>([]);
  const [topMenuItems, setTopMenuItems] = useState<MenuSalesData[]>([]);
  const [bentoTop10, setBentoTop10] = useState<any[]>([]);
  const [hourlySales, setHourlySales] = useState<HourlySalesData[]>([]);
  const [categoryData, setCategoryData] = useState<{name: string; value: number}[]>([]);
  const [summary, setSummary] = useState({
    total_revenue: 0,
    total_transactions: 0,
    average_ticket: 0,
    cash_ratio: 0,
    paypay_ratio: 0
  });
  const [loading, setLoading] = useState(true);

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

  useEffect(() => {
    fetchSalesData();
  }, [period]);

  const fetchSalesData = async () => {
    try {
      setLoading(true);

      // 日別売上データ
      const response = await axios.get(`${getApiUrl()}/analytics/sales`, {
        params: { period }
      });
      console.log('Sales response:', response.data);
      console.log('Current period:', period);
      console.log('API URL:', `${getApiUrl()}/analytics/sales`);

      // データの構造によって処理を分岐
      if (response.data.sales) {
        // 日別売上データ
        setSalesData(response.data.sales || []);

        // サマリー計算
        const sales = response.data.sales || [];
        const totalRevenue = sales.reduce((sum, day) => sum + day.total_sales, 0);
        const totalTransactions = sales.reduce((sum, day) => sum + day.transaction_count, 0);
        const cashSales = sales.reduce((sum, day) => sum + day.cash_sales, 0);
        const paypaySales = sales.reduce((sum, day) => sum + day.paypay_sales, 0);

        setSummary({
          total_revenue: totalRevenue,
          total_transactions: totalTransactions,
          average_ticket: totalTransactions > 0 ? totalRevenue / totalTransactions : 0,
          cash_ratio: totalRevenue > 0 ? (cashSales / totalRevenue) * 100 : 0,
          paypay_ratio: totalRevenue > 0 ? (paypaySales / totalRevenue) * 100 : 0
        });
      } else if (Array.isArray(response.data)) {
        // 配列形式のレスポンスの場合
        setSalesData(response.data);

        // サマリー計算
        const totalRevenue = response.data.reduce((sum, day) => sum + day.total_sales, 0);
        const totalTransactions = response.data.reduce((sum, day) => sum + day.transaction_count, 0);
        const cashSales = response.data.reduce((sum, day) => sum + day.cash_sales, 0);
        const paypaySales = response.data.reduce((sum, day) => sum + day.paypay_sales, 0);

        setSummary({
          total_revenue: totalRevenue,
          total_transactions: totalTransactions,
          average_ticket: totalTransactions > 0 ? totalRevenue / totalTransactions : 0,
          cash_ratio: totalRevenue > 0 ? (cashSales / totalRevenue) * 100 : 0,
          paypay_ratio: totalRevenue > 0 ? (paypaySales / totalRevenue) * 100 : 0
        });
      } else {
        console.warn('Unexpected sales data format:', response.data);
        setSalesData([]);
        setSummary({
          total_revenue: 0,
          total_transactions: 0,
          average_ticket: 0,
          cash_ratio: 0,
          paypay_ratio: 0
        });
      }

      // 人気メニュー
      const menuResponse = await axios.get(`${getApiUrl()}/analytics/top-menu-items`);
      console.log('Menu response:', menuResponse.data);
      setTopMenuItems(menuResponse.data || []);

      // 弁当売上TOP10（期間に応じて取得）
      try {
        let bentoDays = 30;
        if (period === 'day') bentoDays = 1;
        else if (period === 'week') bentoDays = 7;

        const bentoResponse = await axios.get(`${getApiUrl()}/takeout/analytics/popular-menu`, {
          params: { days: bentoDays }
        });
        console.log('Bento response:', bentoResponse.data);
        setBentoTop10(bentoResponse.data.ranking?.slice(0, 10) || []);
      } catch (error) {
        console.error('Failed to fetch bento top 10:', error);
        setBentoTop10([]);
      }

      // 時間帯別売上
      const hourlyResponse = await axios.get(`${getApiUrl()}/analytics/hourly-sales`);
      console.log('Hourly response:', hourlyResponse.data);
      setHourlySales(hourlyResponse.data || []);

      // カテゴリ別売上データを取得（期間パラメータを追加）
      let categoryDays = 7;
      if (period === 'day') categoryDays = 1;
      else if (period === 'month') categoryDays = 30;

      const categoryResponse = await axios.get(`${getApiUrl()}/analytics/category-sales`, {
        params: { days: categoryDays }
      });
      console.log('Category response:', categoryResponse.data);

      // データの構造によって処理を分岐
      if (categoryResponse.data && Array.isArray(categoryResponse.data)) {
        setCategoryData(categoryResponse.data);
      } else if (categoryResponse.data && categoryResponse.data.categories) {
        setCategoryData(categoryResponse.data.categories);
      } else {
        // フォールバック: salesDataからカテゴリデータを生成
        const categoryMap = new Map();
        salesData.forEach(day => {
          // ここでカテゴリ別の集計を行う（実装が必要な場合）
        });
        const categoryDataArray = Array.from(categoryMap.entries()).map(([name, value]) => ({
          name,
          value
        }));
        setCategoryData(categoryDataArray);
      }

    } catch (error) {
      console.error('分析データ取得エラー:', error);
      console.error('エラー詳細:', error.response?.data || error.message);
      // エラー時は空データをセット
      setSalesData([]);
      setSummary({
        total_revenue: 0,
        total_transactions: 0,
        average_ticket: 0,
        cash_ratio: 0,
        paypay_ratio: 0
      });
      setTopMenuItems([]);
      setHourlySales([]);
      setCategoryData([]);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return `¥${value.toLocaleString()}`;
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      // デバッグ用ログ
      console.log('Formatting date:', dateString, '->', date);
      return format(date, 'MM/dd', { locale: ja });
    } catch (error) {
      console.error('Date formatting error:', error);
      return dateString;
    }
  };

  // 決済方法別データを生成
  const paymentData = [
    { name: '現金', value: summary.cash_ratio },
    { name: 'PayPay', value: summary.paypay_ratio }
  ].filter(item => item.value > 0);

  if (loading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            <p className="mt-2 text-gray-600">データを読み込み中...</p>
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">売上分析</h1>
        <div className="flex gap-2">
          <button
            onClick={() => navigate('/home')}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Home className="w-5 h-5" />
            ホーム
          </button>
          <button
            onClick={() => setPeriod('day')}
            className={`px-4 py-2 rounded-lg ${
              period === 'day'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            日別
          </button>
          <button
            onClick={() => setPeriod('week')}
            className={`px-4 py-2 rounded-lg ${
              period === 'week'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            週別
          </button>
          <button
            onClick={() => setPeriod('month')}
            className={`px-4 py-2 rounded-lg ${
              period === 'month'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            月別
          </button>
        </div>
      </div>

      {/* サマリーカード */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600">総売上</div>
          <div className="text-2xl font-bold text-gray-800">
            {formatCurrency(summary.total_revenue)}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600">取引件数</div>
          <div className="text-2xl font-bold text-gray-800">
            {summary.total_transactions}件
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600">平均客単価</div>
          <div className="text-2xl font-bold text-gray-800">
            {formatCurrency(summary.average_ticket)}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600">PayPay比率</div>
          <div className="text-2xl font-bold text-gray-800">
            {summary.paypay_ratio.toFixed(1)}%
          </div>
        </div>
      </div>

      {/* グラフエリア - 上段 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 売上推移 */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">売上推移</h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={salesData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tickFormatter={formatDate} />
              <YAxis />
              <Tooltip formatter={(value) => formatCurrency(Number(value))} />
              <Legend />
              <Line type="monotone" dataKey="total_sales" stroke="#8884d8" name="売上" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* 決済方法別 */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">決済方法別売上</h2>
          {paymentData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={paymentData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${value.toFixed(1)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {paymentData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `${value}%`} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[300px] text-gray-500">
              <p>データがありません</p>
            </div>
          )}
        </div>
      </div>

      {/* グラフエリア - 下段 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 左側: 時間帯別売上と人気メニュー */}
        <div className="flex flex-col gap-6">
          {/* 時間帯別売上 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">時間帯別売上</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={hourlySales}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="hour" />
                <YAxis />
                <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                <Bar dataKey="sales" fill="#82ca9d" name="売上" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* 人気メニューTOP10 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">🍴 人気メニュー TOP10</h2>
            <div className="space-y-3">
              {topMenuItems.slice(0, 10).map((item, index) => (
                <div key={index} className="flex justify-between items-center hover:bg-gray-50 p-2 rounded">
                  <div className="flex items-center">
                    <span className="text-lg font-bold text-gray-400 w-8">
                      {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : index + 1}
                    </span>
                    <span className="text-sm font-medium ml-2">{item.name}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold">{formatCurrency(item.totalSales || item.revenue || 0)}</div>
                    <div className="text-xs text-gray-500">{item.totalQuantity || item.count || 0}個</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 右側: カテゴリ別売上と弁当売上 */}
        <div className="flex flex-col gap-6">
          {/* カテゴリ別売上 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">カテゴリ別売上</h2>
            {categoryData && categoryData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={categoryData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px]">
                <p className="text-gray-500 text-sm">
                  {period === 'day' ? '本日のカテゴリ別売上データがありません' : 'データがありません'}
                </p>
              </div>
            )}
          </div>

          {/* 弁当売上TOP10 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">🍱 弁当売上 TOP10</h2>
            {bentoTop10.length > 0 ? (
              <div className="space-y-3">
                {bentoTop10.map((item, index) => (
                  <div key={index} className="flex justify-between items-center hover:bg-gray-50 p-2 rounded">
                    <div className="flex items-center">
                      <span className="text-lg font-bold text-gray-400 w-8">
                        {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : index + 1}
                      </span>
                      <span className="text-sm font-medium ml-2">{item.menu_name}</span>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold">{formatCurrency(item.total_sales || 0)}</div>
                      <div className="text-xs text-gray-500">{item.total_quantity || 0}個</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                <p className="text-sm">弁当データがありません</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 詳細テーブル */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-semibold">日別売上詳細</h2>
        </div>
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                日付
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                売上
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                件数
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                現金
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                PayPay
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {salesData.map((day, index) => (
              <tr key={index}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatDate(day.date)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                  {formatCurrency(day.total_sales)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                  {day.transaction_count}件
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                  {formatCurrency(day.cash_sales)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                  {formatCurrency(day.paypay_sales)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </div>
    </PageContainer>
  );
};

export default SalesAnalytics;